import * as React from 'react';

import type { W_MaterialCost_FindForWorkSummary_VM_Single } from 'ab-api/web/materialCost/findForWorkSummary';

import { convertUnits } from '@acceligentllc/shared/utils/unitConversion';

import type { QuantityUnitType } from '@acceligentllc/shared/enums/quantityUnit';
import { isPlainUnit } from '@acceligentllc/shared/enums/quantityUnit';

import { dollarFormatter } from 'af-utils/format.util';

import MaterialRateSource from 'af-components/MaterialRateSource';
import Tooltip from 'af-components/Tooltip';

import OriginalUnitPrice from './OriginalUnitPrice';
import styles from './styles.module.scss';
import TotalCostBreakdown from './TotalCostBreakdown';

interface OwnProps {
	materialCost: W_MaterialCost_FindForWorkSummary_VM_Single;
	onDate: string;
	onSelect: (materialCostSource: W_MaterialCost_FindForWorkSummary_VM_Single) => void;
	targetUnit: Nullable<QuantityUnitType>;
}

type Props = OwnProps;

export const MaterialCostEntry: React.FC<Props> = (props) => {
	const { materialCost, onDate, onSelect, targetUnit } = props;

	const { description, vendor, vendorUniqueId, source, totalCost, displayName, unit, baseCost } = materialCost;

	const needsConversion = !!targetUnit && unit !== targetUnit;

	const convertedBaseCost = React.useMemo(() => {
		if (!needsConversion || !isPlainUnit(targetUnit)) {
			return null;
		}
		return convertUnits(baseCost, targetUnit, unit) ?? baseCost;
	}, [needsConversion, targetUnit, baseCost, unit]);

	const convertedTotalCost = React.useMemo(() => {
		if (!needsConversion || !isPlainUnit(targetUnit)) {
			return null;
		}
		return convertUnits(totalCost, targetUnit, unit) ?? totalCost;
	}, [needsConversion, targetUnit, totalCost, unit]);

	const handleSelect = React.useCallback(() => {
		onSelect(materialCost);
	}, [materialCost, onSelect]);

	const hasVendorLine = !!vendor || !!vendorUniqueId;

	return (
		<div className={styles.materialCostEntry} onClick={handleSelect}>
			<div className={styles.firstLine}>
				<div className={styles.sourceNameAndUnit}>
					<MaterialRateSource rateSource={source} />
					<div className={styles.name}>{displayName}</div>
					<div className={styles.unit}>{needsConversion ? targetUnit : unit}</div>
				</div>
				<div className={styles.cost}>
					<Tooltip message={<TotalCostBreakdown convertedBaseCost={convertedBaseCost} materialCost={materialCost} onDate={onDate} />}>
						{dollarFormatter.format(convertedTotalCost ?? totalCost)}
					</Tooltip>
				</div>
			</div>
			<div className={styles.descriptionVendorOriginalPrice}>
				<div className={styles.descriptionVendor}>
					<div className={styles.description}>
						{description}
					</div>
					{hasVendorLine && (
						<div className={styles.vendorLine}>
							Vendor: {vendor ?? <span className={styles.notAssigned}>N/A</span>},&nbsp;
							Vendor ID: {vendorUniqueId ?? <span className={styles.notAssigned}>N/A</span>}
						</div>
					)}
				</div>
				{needsConversion && (
					<div className={styles.originalPrice}>
						<Tooltip message={<OriginalUnitPrice originalUnit={unit} />}>
							{dollarFormatter.format(totalCost)} per {unit}
						</Tooltip>
					</div>
				)}
			</div>
		</div>
	);
};
